import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnChanges, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { ConsentEvent, ConsentEventDocument, ConsentEventsRequestParams } from '@shared/models';
import { FinancialAccountDocumentService, NotificationService } from '@shared/services';
import { ErrorUtils } from '@shared/utils';

import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import { DetailsItemComponent } from '../../details-item/details-item.component';
import { AcceptedDocumentFilePreviewModalComponent } from '../accepted-document-file-preview-modal/accepted-document-file-preview-modal.component';

const CUSTOMER_PAGE_SIZE = 30;
const WORKFLOW_PAGE_SIZE = 100;

@Component({
  selector: 'app-consent-event-history',
  templateUrl: './consent-event-history.component.html',
  styleUrls: ['./consent-event-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RktButtonDirective, RktIconComponent, CdkCopyToClipboard, DetailsItemComponent, DateFormatPipe],
})
export class ConsentEventHistoryComponent implements OnChanges, OnDestroy {
  @Input() acceptorId?: string | null;

  @Input() workflowId?: string | null;

  readonly events = signal<ConsentEvent[]>([]);

  readonly loading = signal(false);

  readonly failed = signal(false);

  readonly hasNext = signal(false);

  private page = 0;

  private request?: Subscription;

  private documentService = inject(FinancialAccountDocumentService);

  private modalService = inject(ModalService);

  private notificationService = inject(NotificationService);

  private destroyRef = inject(DestroyRef);

  get isWorkflowView(): boolean {
    return !!this.workflowId;
  }

  ngOnChanges(): void {
    this.events.set([]);
    this.load(0);
  }

  ngOnDestroy(): void {
    this.modalService.dismissAll();
  }

  loadMore(): void {
    this.load(this.page + 1);
  }

  onPreview({ downloadUrl, documentName, version }: ConsentEventDocument): void {
    const previewModal = this.modalService.open(AcceptedDocumentFilePreviewModalComponent, {
      className: 'entity-form-modal documents-modal',
    });

    previewModal.componentInstance.modalInitData({ downloadUrl, name: documentName, version });

    previewModal.result.then(
      (response) => {
        if (response?.type === 'error') {
          this.notificationService.displayError('Unable to download file: ' + response?.message);
          ErrorUtils.catchError('consentEventHistory', `${response?.name}: ${response?.message}`);
        }
      },
      () => false,
    );
  }

  shortHash(sha256: string): string {
    return sha256.length > 24 ? `${sha256.slice(0, 12)}…${sha256.slice(-8)}` : sha256;
  }

  onHashCopied(): void {
    this.notificationService.displaySuccess('Hash copied to clipboard');
  }

  onDownload({ downloadUrl, documentName, version, fileExtension }: ConsentEventDocument): void {
    if (!downloadUrl) return;

    this.documentService
      .getAcceptanceDocument(downloadUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ body, headers }) => {
          const file = new Blob([body], { type: headers.get('Content-Type') ?? 'application/octet-stream' });
          const objectUrl = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = `${documentName} v${version}${fileExtension ? '.' + fileExtension : ''}`;
          link.click();
          URL.revokeObjectURL(objectUrl);
        },
        error: (error) => {
          this.notificationService.displayError('Unable to download file.');
          ErrorUtils.catchError('financialAccountDocumentService.getAcceptanceDocument error', error);
        },
      });
  }

  private requestParams(page: number): ConsentEventsRequestParams | undefined {
    if (this.workflowId) {
      return { workflowId: this.workflowId, page, size: WORKFLOW_PAGE_SIZE, sort: 'respondedAt,asc' };
    }
    if (this.acceptorId) {
      return { acceptorType: 'CUSTOMER', acceptorId: this.acceptorId, page, size: CUSTOMER_PAGE_SIZE };
    }
    return undefined;
  }

  private load(page: number): void {
    this.request?.unsubscribe();
    const params = this.requestParams(page);
    if (!params) {
      this.loading.set(false);
      this.hasNext.set(false);
      return;
    }

    this.loading.set(true);
    this.failed.set(false);

    this.request = this.documentService
      .getConsentEvents(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ items, hasNext }) => {
          this.page = page;
          this.events.update((events) => (page === 0 ? items : [...events, ...items]));
          this.hasNext.set(!!hasNext);
          this.loading.set(false);
        },
        error: (error) => {
          ErrorUtils.catchError('financialAccountDocumentService.getConsentEvents error', error);
          this.failed.set(true);
          this.loading.set(false);
        },
      });
  }
}
